import axios from 'axios';
import type { AppDispatch, RootState } from '@/app/store';
import { experienceApiEndpoint } from '@/config/experienceApiEndpoint';
import type { DaGatewayDigitalApplicationFeaturesDto, DaGatewayTenantConfigResponse } from '@/services/lp/contracts';
import { cloneDeep, merge } from 'lodash-es';
import { handleErrors, createEnhancedError } from '../utils/handleErrors';
import { LoanApplication } from '@/reducers/LoanApplication';

export const endpointFront = experienceApiEndpoint;

interface ConfigProps {
  losIntegration: string;
  ui: {
    isICE: boolean;
    isLpGateway?: boolean;
    pages?: Record<string, unknown>;
    features: {
      vmoRedesign?: object;
      resume?: object;
      employmentVerification?: object;
      vmoDebtConsolidation?: object;
      preventTests?: object;
    };
    fixedValues: Record<string, any>;
    companyUrl?: string;
    companyInfo?: {
      url?: string;
      name?: string;
      alias?: string;
    };
  };
  features: {
    digitalApplication?: DaGatewayDigitalApplicationFeaturesDto;
    vmoRedesign?: object;
    resume?: object;
    employmentVerification?: object;
    vmoDebtConsolidation?: object;
    preventTests?: object;
  };
  trackers?: {
    gtm?: { id: string }[];
  };
}

export default class ExperienceService {
  private dispatch: AppDispatch;
  private getState: () => RootState;
  isICE: boolean;
  isLpGateway: boolean;

  constructor(dispatch: AppDispatch, getState: () => RootState) {
    this.dispatch = dispatch;
    this.getState = getState;
    this.isICE = getState().TemplateConfig?.isICE === true;
    this.isLpGateway = getState().TemplateConfig?.isLpGateway === true;
  }

  getApiBaseUrl = () => {
    return this.isICE ? experienceApiEndpoint.experienceApi.replace('equityiq', 'equity-iq-ice') : experienceApiEndpoint.experienceApi;
  };

  getApiBaseGlobalConfig = () => {
    return experienceApiEndpoint.experienceApiConfig.replace('equity-iq-ice', 'equityiq');
  };

  updateGlobalConfig = (response: ConfigProps) => {
    const { ui, features, losIntegration, trackers } = response;
    const cloneResponse = { ...ui };
    const clonedLocalLoanApplication = cloneDeep(LoanApplication);

    const newRelicConfig = (ui.pages as { newRelic?: unknown })?.newRelic;

    this.isICE = losIntegration === 'encompass';
    this.isLpGateway = true;
    cloneResponse.isICE = this.isICE;
    cloneResponse.isLpGateway = this.isLpGateway;
    cloneResponse.features = merge({}, cloneResponse.features, features);

    if (newRelicConfig) {
      if (!cloneResponse.pages) {
        cloneResponse.pages = {};
      }
      const pages = cloneResponse.pages as Record<string, unknown>;
      if (!pages.features) {
        pages.features = {};
      }
      (pages.features as Record<string, unknown>).newRelic = newRelicConfig;
    }

    if (trackers) {
      (cloneResponse as any).trackers = trackers;
    }

    const resolvedCompanyUrl = cloneResponse.companyInfo?.url || cloneResponse.companyUrl;
    if (resolvedCompanyUrl) {
      cloneResponse.companyUrl = resolvedCompanyUrl;
      cloneResponse.companyInfo = { ...cloneResponse.companyInfo, url: resolvedCompanyUrl };
    }

    experienceApiEndpoint.experienceApi = this.getApiBaseUrl();
    experienceApiEndpoint.experienceApiConfig = this.getApiBaseGlobalConfig();

    if (ui.fixedValues && Object.keys(ui.fixedValues).length > 0) {
      Object.entries(ui.fixedValues).forEach(([key, value]) => {
        if (clonedLocalLoanApplication.getInitialState().hasOwnProperty(key)) {
          this.dispatch({
            type: 'LoanApplication/updateSpecificField',
            payload: {
              depth: [key],
              value,
            },
          });
        }
      });
    }

    this.dispatch({
      type: 'TemplateConfig/updateTemplateConfig',
      payload: cloneResponse,
    });
  };

  getGlobalConfig = async () => {
    endpointFront.experienceApiConfig = this.getApiBaseGlobalConfig();
    const configUrl = `${endpointFront.experienceApiConfig}/api/v1/config`;

    try {
      const gatewayBaseUrl = (endpointFront.daGatewayApi || this.getApiBaseUrl()).replace(/\/+$/, '').replace(/\/api\/v1$/i, '');
      const [response, tenantConfig] = await Promise.all([
        axios.get<ConfigProps>(configUrl, this.config),
        axios.get<DaGatewayTenantConfigResponse>(`${gatewayBaseUrl}/api/v1/tenant/config`, {
          headers: {
            ...this.config.headers,
            ...(endpointFront.daGatewayDevelopmentOrigin ? { 'Development-Origin': endpointFront.daGatewayDevelopmentOrigin } : {}),
          },
        }),
      ]);
      const digitalApplication = tenantConfig.data.features?.digitalApplication;
      if (typeof digitalApplication?.resume !== 'boolean' || typeof digitalApplication?.resumeOnly !== 'boolean') {
        throw new Error('Digital Application Resume configuration is missing or invalid.');
      }
      this.updateGlobalConfig({
        ...response.data,
        features: { ...response.data.features, digitalApplication },
      });
    } catch (error) {
      const detailsError = handleErrors(error, false);
      const enhancedError = createEnhancedError(detailsError);
      throw enhancedError;
    }
  };

  config = {
    headers: {
      'api-key': endpointFront.apiKey || 'none',
      ...(endpointFront.mocked ? { 'mocked-origin': endpointFront.mocked } : {}),
    },
  };
}
